import { useAuth0 } from '@auth0/auth0-react';
import ReactLazyLoading from '@components/ReactLazyLoading';
import { AUTH_FALLBACK } from '@const';
import { useSubmitAuthErrorMutation } from '@service';
import { RootState } from '@store';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import yn from 'yn';
import { sec } from './security';

const { VITE_DISABLE_AUTH } = import.meta.env;

export interface AppAuthProps {
  children: React.ReactNode | React.ReactNode[];
}
function AppAuth({ children }: AppAuthProps) {
  const navigate = useNavigate();

  const [submitAuthError] = useSubmitAuthErrorMutation();
  const auth0Data = useAuth0();

  const session_id = useSelector((state: RootState) => state.slice.sessionId);
  const reportedError = useRef<string | null>(null);
  const {
    // \n
    error,
    getAccessTokenSilently,
    isLoading,
    loginWithRedirect,
    user,
    logout,
    isAuthenticated
  } = yn(VITE_DISABLE_AUTH) ? AUTH_FALLBACK : auth0Data;

  window.logout = logout;

  sec.setAccessTokenSilently(async () => (await getAccessTokenSilently()) ?? '');

  useEffect(() => {
    if (error) {
      if (reportedError.current !== error.message) {
        reportedError.current = error.message;
        const oauthError = error as Error & { error?: string; error_description?: string };
        const parts = [`useAuth0() middleware threw an error: [${error.name}] ${error.message}`];

        if (oauthError.error) parts.push(`oauth_error: ${oauthError.error}`);
        if (oauthError.error_description) parts.push(`description: ${oauthError.error_description}`);

        parts.push(`url: ${window.location.pathname}`);
        submitAuthError({ error_message: parts.join(' | '), session_id });
        toast.error('failed to authenticate w/ auth0, check console for error if possible');
        console.error(error);

        // "Invalid state" means the callback landed on a different origin than
        // where login was initiated (cross-origin localStorage isolation). Clear
        // the stale code/state params from the URL and restart the login flow
        // so the SDK doesn't loop on the same broken callback indefinitely.
        if (error.message === 'Invalid state') {
          const clean = new URL(window.location.href);
          clean.searchParams.delete('code');
          clean.searchParams.delete('state');
          window.history.replaceState({}, '', clean.toString());
          loginWithRedirect();
        }
      }
      return;
    }
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, error, loginWithRedirect, isAuthenticated, submitAuthError, session_id]);

  const url = new URL(window.location.href);
  const redirectPath = url.searchParams.get('path');

  // check url parameters for `path`:
  useEffect(() => {
    if (redirectPath && user) {
      // Navigate to the new path
      navigate(redirectPath);
    }
  }, [navigate, redirectPath, user]);

  // don't render anything until we know if the user is authenticated:
  if (isLoading || !user || redirectPath) {
    return <ReactLazyLoading />;
  }

  return children;
}

export default AppAuth;
